var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { MonetaryAmount } from "@interlay/monetary-js";
import { addressOrPairAsAccountId, calculateAnnualizedRewardAmount, currencyIdToMonetaryCurrency, decodeBytesAsString, decodeFixedPointType, getStandardLpTokenFromCurrencyId, isCurrencyEqual, isStableLpToken, monetaryAmountToRawString, newCurrencyId, newMonetaryAmount, storageKeyToNthInner, } from "../utils";
import Big from "big.js";
import { getAllTradingPairs, findBestTradeRecursively, StandardLiquidityPool, StableLiquidityPool, PoolType, encodeSwapParamsForStandardPoolsOnly, encodeSwapParamsForStandardAndStablePools, isStableMultiPathElement, isStableMetaPool, isStandardPool, StableLiquidityMetaPool, } from "./amm/";
const HOP_LIMIT = 4;
const FEE_MULTIPLIER_STANDARD = 10000;
const FEE_MULTIPLIER_STABLE = 10000000000;
export class DefaultAMMAPI {
    static getStablePoolInfo(poolData) {
        if (poolData.isBase) {
            return poolData.asBase;
        }
        if (poolData.isMeta) {
            return poolData.asMeta.info;
        }
        return null;
    }
    static getStableLpTokenFromPoolData(poolId, basePoolData) {
        const [ticker, decimals] = [
            decodeBytesAsString(basePoolData.lpCurrencySymbol),
            basePoolData.lpCurrencyDecimal.toNumber(),
        ];
        return {
            name: ticker,
            ticker,
            decimals,
            stableLpToken: {
                poolId,
            },
        };
    }
    constructor(api, tokensAPI) {
        this.api = api;
        this.tokensAPI = tokensAPI;
    }
    getOptimalTrade(inputAmount, outputCurrency, pools) {
        const pairs = getAllTradingPairs(pools);
        if (pairs.length === 0 || inputAmount.isZero()) {
            return null;
        }
        return findBestTradeRecursively(inputAmount, outputCurrency, pairs, HOP_LIMIT);
    }
    getLiquidityProvidedByAccount(accountId) {
        return __awaiter(this, void 0, void 0, function* () {
            const allLpTokens = yield this.getLpTokens();
            const accountBalances = yield Promise.all(allLpTokens.map((lpToken) => this.tokensAPI.balance(lpToken, accountId)));
            // Adds free and staked balances together.
            return accountBalances.map((balance) => balance.free.add(balance.reserved));
        });
    }
    _getStandardLpTokens() {
        return __awaiter(this, void 0, void 0, function* () {
            const standardPools = yield this.api.query.dexGeneral.liquidityPairs.entries();
            const standardLpTokens = yield Promise.all(standardPools.map(([_, lpTokenCurrencyId]) => getStandardLpTokenFromCurrencyId(this.api, lpTokenCurrencyId.unwrap())));
            return standardLpTokens;
        });
    }
    _getStableLpTokens() {
        return __awaiter(this, void 0, void 0, function* () {
            const stablePools = yield this.api.query.dexStable.pools.entries();
            const stableLpTokens = stablePools.map(([key, poolData]) => {
                if (!poolData.isSome) {
                    return null;
                }
                const poolBase = DefaultAMMAPI.getStablePoolInfo(poolData.unwrap());
                if (poolBase === null) {
                    return null;
                }
                return DefaultAMMAPI.getStableLpTokenFromPoolData(storageKeyToNthInner(key).toNumber(), poolBase);
            });
            return stableLpTokens.filter((token) => token !== null);
        });
    }
    getLpTokens() {
        return __awaiter(this, void 0, void 0, function* () {
            const [standardLpTokens, stableLpTokens] = yield Promise.all([
                this._getStandardLpTokens(),
                this._getStableLpTokens(),
            ]);
            return [...standardLpTokens, ...stableLpTokens];
        });
    }
    _poolHasZeroLiquidity(pooledCurrencies) {
        return pooledCurrencies.some((amount) => amount.isZero());
    }
    _getStandardPoolReserveBalances(token0, token1, pairAccount) {
        return __awaiter(this, void 0, void 0, function* () {
            const [token0Balance, token1Balance] = yield Promise.all([
                this.tokensAPI.balance(token0, pairAccount),
                this.tokensAPI.balance(token1, pairAccount),
            ]);
            const token0MonetaryAmount = token0Balance.free;
            const token1MonetaryAmount = token1Balance.free;
            return [token0MonetaryAmount, token1MonetaryAmount];
        });
    }
    _getPoolRewardAmountsYearly(lpTokenCurrencyId, blockTimeMs) {
        return __awaiter(this, void 0, void 0, function* () {
            const rewardPeriod = this.api.consts.farming.rewardPeriod;
            const rewardsRaw = yield this.api.query.farming.rewardSchedules.entries(lpTokenCurrencyId);
            const rewardAmountsYearly = yield Promise.all(rewardsRaw.map(([key, value]) => __awaiter(this, void 0, void 0, function* () {
                const rewardCurrencyId = storageKeyToNthInner(key, 1);
                const rewardCurrency = yield currencyIdToMonetaryCurrency(this.api, rewardCurrencyId);
                const amountPerBlock = Big(value.perPeriod.toString()).div(rewardPeriod.toNumber());
                const annualizedRewardAmount = calculateAnnualizedRewardAmount(amountPerBlock, blockTimeMs);
                return newMonetaryAmount(annualizedRewardAmount, rewardCurrency);
            })));
            return rewardAmountsYearly;
        });
    }
    _getStandardLiquidityPool(pairCurrencies, lpTokenCurrencyId, pairStatus, blockTimeMs) {
        return __awaiter(this, void 0, void 0, function* () {
            let typedPairStatus;
            let isTradingActive;
            let tradingFee;
            let totalSupplyAmount;
            if (pairStatus.isTrading) {
                typedPairStatus = pairStatus.asTrading;
                isTradingActive = true;
                tradingFee = Big(typedPairStatus.feeRate.toString()).div(FEE_MULTIPLIER_STANDARD);
                // NOTE: this is a hacky way to convert totalSupply since it assumes
                // we always use a precision of 18 for lpTokens, refactor this
                totalSupplyAmount = decodeFixedPointType(typedPairStatus.totalSupply);
            }
            else if (pairStatus.isBootstrap) {
                typedPairStatus = pairStatus.asBootstrap;
                isTradingActive = false;
                tradingFee = Big(0);
                totalSupplyAmount = Big(0);
            }
            else {
                return null;
            }
            const pairAccount = typedPairStatus.pairAccount;
            const [token0, token1] = yield Promise.all(pairCurrencies.map((currency) => currencyIdToMonetaryCurrency(this.api, currency)));
            const [lpToken, pooledCurrencies, yearlyRewards] = yield Promise.all([
                getStandardLpTokenFromCurrencyId(this.api, lpTokenCurrencyId),
                this._getStandardPoolReserveBalances(token0, token1, pairAccount),
                this._getPoolRewardAmountsYearly(lpTokenCurrencyId, blockTimeMs),
            ]);
            const isEmpty = this._poolHasZeroLiquidity(pooledCurrencies);
            const totalSupply = new MonetaryAmount(lpToken, totalSupplyAmount);
            return new StandardLiquidityPool(lpToken, pooledCurrencies, yearlyRewards, tradingFee, isTradingActive, totalSupply, isEmpty);
        });
    }
    getStandardLiquidityPools(blockTimeMs) {
        return __awaiter(this, void 0, void 0, function* () {
            const pairEntries = yield this.api.query.dexGeneral.liquidityPairs.entries();
            const pairs = pairEntries.filter(([_, lpToken]) => lpToken.isSome);
            const pairStatuses = yield Promise.all(pairs.map(([pairKey]) => this.api.query.dexGeneral.pairStatuses(storageKeyToNthInner(pairKey))));
            const pools = yield Promise.all(pairs.map(([pairKey, lpToken], index) => this._getStandardLiquidityPool(storageKeyToNthInner(pairKey), lpToken.unwrap(), pairStatuses[index], blockTimeMs)));
            return pools.filter((pool) => pool !== null);
        });
    }
    _getStablePoolPooledCurrencies(currencyIds, balances) {
        return __awaiter(this, void 0, void 0, function* () {
            const pooledMonetaryCurrencies = yield Promise.all(currencyIds.map((currencyId) => currencyIdToMonetaryCurrency(this.api, currencyId)));
            const pooledCurrencies = pooledMonetaryCurrencies.map((currency, index) => newMonetaryAmount(Big(balances[index].toString()), currency));
            return pooledCurrencies;
        });
    }
    _getStablePoolAmplificationCoefficient(poolId) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            // TODO: refactor when RPC call is added to node
            // const rawA = await this.api.rpc.dexStable.getA(poolId);
            // return decodeNumberOrHex(rawA);
            const poolData = yield this.api.query.dexStable.pools(poolId);
            if (poolData.isSome) {
                const rawA = (_a = DefaultAMMAPI.getStablePoolInfo(poolData.unwrap())) === null || _a === void 0 ? void 0 : _a.futureA;
                if (rawA !== undefined) {
                    return Big(rawA.toString());
                }
            }
            throw new Error(`_getStablePoolAmplificationCoefficient: Invalid pool id ${poolId}.`);
        });
    }
    _getStableBasePooledCurrenciesAdjustedToLpTokenAmount(basePooledCurrencies, lpTokenTotalSupply, metaPoolLpTokenAmount) {
        const changeCoefficient = metaPoolLpTokenAmount.div(lpTokenTotalSupply.toBig()).toBig();
        return basePooledCurrencies.map((amount) => amount.mul(changeCoefficient));
    }
    _getStableLiquidityPoolData(poolId, poolData, blockTimeMs, metaPoolLpTokenAmount) {
        return __awaiter(this, void 0, void 0, function* () {
            const poolInfo = DefaultAMMAPI.getStablePoolInfo(poolData);
            if (poolInfo === null) {
                return null;
            }
            const [pooledCurrencyIds, pooledCurrencyBalances, tradingFee, lpTokenCurrencyId] = [
                poolInfo.currencyIds,
                poolInfo.balances,
                Big(poolInfo.fee.toString()).div(FEE_MULTIPLIER_STABLE),
                poolInfo.lpCurrencyId,
            ];
            const lpToken = DefaultAMMAPI.getStableLpTokenFromPoolData(poolId, poolInfo);
            const [pooledCurrenciesBase, yearlyRewards, amplificationCoefficient, totalSupply] = yield Promise.all([
                this._getStablePoolPooledCurrencies(pooledCurrencyIds, pooledCurrencyBalances),
                this._getPoolRewardAmountsYearly(lpTokenCurrencyId, blockTimeMs),
                this._getStablePoolAmplificationCoefficient(poolId),
                this.tokensAPI.total(lpToken),
            ]);
            // Adjust currency amounts based on LP token amount of metapool.
            const actuallyPooledCurrencies = metaPoolLpTokenAmount !== undefined
                ? this._getStableBasePooledCurrenciesAdjustedToLpTokenAmount(pooledCurrenciesBase, totalSupply, metaPoolLpTokenAmount)
                : pooledCurrenciesBase;
            return { lpToken, actuallyPooledCurrencies, yearlyRewards, amplificationCoefficient, totalSupply, tradingFee };
        });
    }
    _getStableMetaPoolBasePool(poolData, pooledCurrencies, blockTimeMs) {
        return __awaiter(this, void 0, void 0, function* () {
            const basePoolId = poolData.basePoolId;
            const basePoolData = yield this.api.query.dexStable.pools(basePoolId);
            const pooledLpTokenAmount = (pooledCurrencies.find((amount) => isStableLpToken(amount.currency)));
            if (basePoolData.isSome) {
                if (basePoolData.unwrap().isMeta) {
                    throw new Error("Nested metapools are not supported.");
                }
                const basePool = yield this._getStableLiquidityPool(basePoolId.toNumber(), basePoolData.unwrap(), blockTimeMs, pooledLpTokenAmount);
                if (basePool === null) {
                    throw new Error("Metapool's base pool data are not valid.");
                }
                return basePool;
            }
            throw new Error(`Base pool with id ${basePoolId} does not exist.`);
        });
    }
    _getStableLiquidityPool(poolId, poolData, blockTimeMs, metaPoolLpTokenAmount) {
        return __awaiter(this, void 0, void 0, function* () {
            const processedPoolData = yield this._getStableLiquidityPoolData(poolId, poolData, blockTimeMs, metaPoolLpTokenAmount);
            if (processedPoolData === null) {
                return null;
            }
            const { lpToken, actuallyPooledCurrencies, yearlyRewards, tradingFee, amplificationCoefficient, totalSupply } = processedPoolData;
            const isEmpty = this._poolHasZeroLiquidity(actuallyPooledCurrencies);
            if (poolData.isBase) {
                return new StableLiquidityPool(PoolType.STABLE_PLAIN, lpToken, actuallyPooledCurrencies, actuallyPooledCurrencies, yearlyRewards, tradingFee, poolId, amplificationCoefficient, totalSupply, isEmpty);
            }
            // When pool is metapool, nested base pool instance is created.
            const basePool = yield this._getStableMetaPoolBasePool(poolData.asMeta, actuallyPooledCurrencies, blockTimeMs);
            const pooledCurrencies = actuallyPooledCurrencies.reduce((result, currentAmount) => isStableLpToken(currentAmount.currency)
                ? [...result, ...basePool.pooledCurrencies]
                : [...result, currentAmount], []);
            return new StableLiquidityMetaPool(lpToken, actuallyPooledCurrencies, pooledCurrencies, yearlyRewards, tradingFee, poolId, amplificationCoefficient, totalSupply, isEmpty, basePool);
        });
    }
    getStableLiquidityPools(blockTimeMs) {
        return __awaiter(this, void 0, void 0, function* () {
            const poolEntries = yield this.api.query.dexStable.pools.entries();
            const rawPoolsData = poolEntries.filter(([_, pool]) => pool.isSome);
            const pools = yield Promise.all(rawPoolsData.map(([poolId, poolData]) => this._getStableLiquidityPool(storageKeyToNthInner(poolId).toNumber(), poolData.unwrap(), blockTimeMs)));
            return pools.filter((pool) => pool !== null);
        });
    }
    getLiquidityPools() {
        return __awaiter(this, void 0, void 0, function* () {
            const blockTimeMs = (yield this.api.call.auraApi.slotDuration()).toNumber();
            const [standardPools, stablePools] = yield Promise.all([
                this.getStandardLiquidityPools(blockTimeMs),
                this.getStableLiquidityPools(blockTimeMs),
            ]);
            return [...standardPools, ...stablePools];
        });
    }
    _swapThroughStandardPoolsOnly(trade, minimumAmountOut, recipient, deadline) {
        const { amountIn, amountOutMin, path } = encodeSwapParamsForStandardPoolsOnly(this.api, trade, minimumAmountOut);
        const swapExtrinsic = this.api.tx.dexGeneral.swapExactAssetsForAssets(amountIn, amountOutMin, path, addressOrPairAsAccountId(this.api, recipient), deadline);
        const swapEvent = this.api.events.dexGeneral.AssetSwap;
        return { extrinsic: swapExtrinsic, event: swapEvent };
    }
    _swapThroughStandardAndStablePools(trade, minimumAmountOut, recipient, deadline) {
        const { amountIn, amountOutMin, path } = encodeSwapParamsForStandardAndStablePools(this.api, trade, minimumAmountOut);
        const swapExtrinsic = this.api.tx.dexSwapRouter.swapExactTokenForTokensThroughStablePool(amountIn, amountOutMin, path, addressOrPairAsAccountId(this.api, recipient), deadline);
        return { extrinsic: swapExtrinsic, event: this.api.events.dexStable.CurrencyExchange };
    }
    _getFarmingRewardCurrencyIds(lpTokenCurrencyId) {
        return __awaiter(this, void 0, void 0, function* () {
            const rewardCurrenciesRaw = yield this.api.query.farmingRewards.rewardCurrencies(lpTokenCurrencyId);
            const rewardCurrencies = Array.from(rewardCurrenciesRaw.values());
            return rewardCurrencies;
        });
    }
    _getClaimableFarmingRewardsByPool(accountId, lpToken) {
        return __awaiter(this, void 0, void 0, function* () {
            const lpTokenCurrencyId = newCurrencyId(this.api, lpToken);
            const rewardCurrencyIds = yield this._getFarmingRewardCurrencyIds(lpTokenCurrencyId);
            const farmingRewards = yield Promise.all(rewardCurrencyIds.map((rewardCurrencyId) => this.api.rpc.reward.computeFarmingReward(accountId, lpTokenCurrencyId, rewardCurrencyId)));
            const rewardAmounts = Promise.all(rewardCurrencyIds.map((currencyId, index) => __awaiter(this, void 0, void 0, function* () {
                const currency = yield currencyIdToMonetaryCurrency(this.api, currencyId);
                return newMonetaryAmount(farmingRewards[index].amount.toString(), currency);
            })));
            return rewardAmounts;
        });
    }
    getClaimableFarmingRewards(accountId, accountLiquidity, pools) {
        return __awaiter(this, void 0, void 0, function* () {
            const rewardAmounts = yield Promise.all(accountLiquidity.map(({ currency }) => {
                const pool = pools.find((poolData) => isCurrencyEqual(poolData.lpToken, currency));
                if (pool === undefined) {
                    // Return empty array for pools without liquidity.
                    return [];
                }
                return this._getClaimableFarmingRewardsByPool(accountId, currency);
            }));
            const claimableRewards = new Map();
            rewardAmounts.forEach((rewards, index) => {
                const lpToken = accountLiquidity[index].currency;
                claimableRewards.set(lpToken, rewards);
            });
            return claimableRewards;
        });
    }
    swap(trade, minimumAmountOut, recipient, deadline) {
        const containsStablePool = trade.path.some(isStableMultiPathElement);
        if (containsStablePool) {
            return this._swapThroughStandardAndStablePools(trade, minimumAmountOut, recipient, deadline);
        }
        else {
            return this._swapThroughStandardPoolsOnly(trade, minimumAmountOut, recipient, deadline);
        }
    }
    _getLiquidityDepositStandardPoolParams(amounts, pool, maxSlippageComplement, deadline) {
        if (amounts.length !== 2) {
            throw new Error("Invalid count of input amounts.");
        }
        if (!isCurrencyEqual(pool.token0, amounts[0].currency) || !isCurrencyEqual(pool.token1, amounts[1].currency)) {
            throw new Error("Input currencies and pool currencies differ.");
        }
        const minAmounts = amounts.map((amount) => amount.mul(maxSlippageComplement));
        const [asset0, asset1, amount0Desired, amount1Desired, amount0Min, amount1Min] = [
            newCurrencyId(this.api, amounts[0].currency),
            newCurrencyId(this.api, amounts[1].currency),
            amounts[0].toString(true),
            amounts[1].toString(true),
            minAmounts[0].toString(true),
            minAmounts[1].toString(true),
        ];
        const addLiquidityToStandardPoolExtrinsic = this.api.tx.dexGeneral.addLiquidity(asset0, asset1, amount0Desired, amount1Desired, amount0Min, amount1Min, deadline);
        return { extrinsic: addLiquidityToStandardPoolExtrinsic, event: this.api.events.dexGeneral.LiquidityAdded };
    }
    _getLiquidityDepositStablePoolParams(amounts, pool, maxSlippageComplement, deadline, recipient) {
        const minAmounts = amounts.map((amount) => amount.mul(maxSlippageComplement));
        const minimumLpTokenOut = pool.getLiquidityDepositLpTokenAmount(minAmounts[0]).toString(true);
        const recipientAccount = addressOrPairAsAccountId(this.api, recipient);
        if (!isStableMetaPool(pool)) {
            amounts.forEach((amount, index) => {
                if (!isCurrencyEqual(pool.pooledCurrencies[index].currency, amount.currency)) {
                    throw new Error(`Invalid input amounts, currency ${amount.currency.ticker} is not at index ${index} of pool.`);
                }
            });
            const rawAmounts = amounts.map(monetaryAmountToRawString);
            const addLiquidityToStablePoolExtrinsic = this.api.tx.dexStable.addLiquidity(pool.poolId, rawAmounts, minimumLpTokenOut, recipientAccount, deadline);
            return { extrinsic: addLiquidityToStablePoolExtrinsic, event: this.api.events.dexStable.AddLiquidity };
        }
        // Pass 0 for LP token amount - this will be automatically changed base on how much
        // liquidity is really added to base pool.
        const metaAmounts = pool.actuallyPooledCurrencies.map((actuallyPooledCurrency) => amounts.find((amount) => isCurrencyEqual(amount.currency, actuallyPooledCurrency.currency)) ||
            new MonetaryAmount(actuallyPooledCurrency.currency, 0));
        const rawMetaAmounts = metaAmounts.map(monetaryAmountToRawString);
        const baseAmounts = amounts.filter((amount) => pool.basePool.involvesToken(amount.currency));
        const rawBaseAmounts = baseAmounts.map(monetaryAmountToRawString);
        if (metaAmounts.length + baseAmounts.length !==
            pool.actuallyPooledCurrencies.length + pool.basePool.actuallyPooledCurrencies.length) {
            throw new Error("Invalid input amounts.");
        }
        const addLiquidityToStableMetaPoolExtrinsic = this.api.tx.dexStable.addPoolAndBasePoolLiquidity(pool.poolId, pool.basePool.poolId, rawMetaAmounts, rawBaseAmounts, minimumLpTokenOut, recipientAccount, deadline);
        return { extrinsic: addLiquidityToStableMetaPoolExtrinsic, event: this.api.events.dexStable.AddLiquidity };
    }
    addLiquidity(amounts, pool, maxSlippage, deadline, recipient) {
        return __awaiter(this, void 0, void 0, function* () {
            const maxSlippageComplement = 1 - maxSlippage / 100;
            let depositExtrinsic;
            let depositEvent;
            if (isStandardPool(pool)) {
                ({ extrinsic: depositExtrinsic, event: depositEvent } = this._getLiquidityDepositStandardPoolParams(amounts, pool, maxSlippageComplement, deadline));
            }
            else {
                ({ extrinsic: depositExtrinsic, event: depositEvent } = this._getLiquidityDepositStablePoolParams(amounts, pool, maxSlippageComplement, deadline, recipient));
            }
            const lpTokenCurrencyId = newCurrencyId(this.api, pool.lpToken);
            const rewardCurrencies = yield this.api.query.farmingRewards.rewardCurrencies(lpTokenCurrencyId);
            const farmDepositExtrinsic = this.api.tx.farming.deposit(lpTokenCurrencyId, rewardCurrencies.size);
            const batchedExtrinsics = this.api.tx.utility.batchAll([depositExtrinsic, farmDepositExtrinsic]);
            return { extrinsic: batchedExtrinsics, event: depositEvent };
        });
    }
    _getLiquidityWithdrawalStandardPoolParams(amount, pool, maxSlippageComplement, recipient, deadline) {
        const outputAmounts = pool.getLiquidityWithdrawalPooledCurrencyAmounts(amount);
        const minAmounts = outputAmounts.map((amount) => amount.mul(maxSlippageComplement).toString(true));
        const recipientAccount = addressOrPairAsAccountId(this.api, recipient);
        const withdrawalExtrinsic = this.api.tx.dexGeneral.removeLiquidity(newCurrencyId(this.api, pool.token0), newCurrencyId(this.api, pool.token1), amount.toString(true), minAmounts[0], minAmounts[1], recipientAccount, deadline);
        return { extrinsic: withdrawalExtrinsic, event: this.api.events.dexGeneral.LiquidityRemoved };
    }
    _getLiquidityWithdrawalStablePoolParams(amount, pool, maxSlippageComplement, recipient, deadline) {
        const outputAmounts = pool.getLiquidityWithdrawalPooledCurrencyAmounts(amount);
        const minAmounts = outputAmounts.map((amount) => amount.mul(maxSlippageComplement));
        const poolId = pool.poolId;
        const lpTokenAmount = amount.toString(true);
        const recipientAccount = addressOrPairAsAccountId(this.api, recipient);
        if (isStableMetaPool(pool)) {
            const basePoolId = pool.basePool.poolId;
            const minAmountsMeta = pool.actuallyPooledCurrencies
                .map((actuallyPooledCurrency) => minAmounts.find((amount) => isCurrencyEqual(amount.currency, actuallyPooledCurrency.currency)) || new MonetaryAmount(actuallyPooledCurrency.currency, 0))
                .map(monetaryAmountToRawString);
            const minAmountsBase = minAmounts
                .filter((amount) => pool.basePool.involvesToken(amount.currency))
                .map(monetaryAmountToRawString);
            const withdrawLiquidityExtrinsic = this.api.tx.dexStable.removePoolAndBasePoolLiquidity(poolId, basePoolId, lpTokenAmount, minAmountsMeta, minAmountsBase, recipientAccount, deadline);
            return { extrinsic: withdrawLiquidityExtrinsic, event: this.api.events.dexStable.RemoveLiquidity };
        }
        const minAmountsRaw = minAmounts.map(monetaryAmountToRawString);
        const withdrawLiquidityExtrinsic = this.api.tx.dexStable.removeLiquidity(poolId, lpTokenAmount, minAmountsRaw, recipientAccount, deadline);
        return { extrinsic: withdrawLiquidityExtrinsic, event: this.api.events.dexStable.RemoveLiquidity };
    }
    removeLiquidity(amount, pool, maxSlippage, // Percentage.
    deadline, recipient) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!isCurrencyEqual(amount.currency, pool.lpToken)) {
                throw new Error(`Input amount and pool lp token should be same but are: [${amount.currency.ticker}, ${pool.lpToken.ticker}].`);
            }
            const maxSlippageComplement = 1 - maxSlippage / 100;
            let withdrawalExtrinsic;
            let withdrawalEvent;
            if (isStandardPool(pool)) {
                ({ extrinsic: withdrawalExtrinsic, event: withdrawalEvent } =
                    this._getLiquidityWithdrawalStandardPoolParams(amount, pool, maxSlippageComplement, recipient, deadline));
            }
            else {
                ({ extrinsic: withdrawalExtrinsic, event: withdrawalEvent } = this._getLiquidityWithdrawalStablePoolParams(amount, pool, maxSlippageComplement, recipient, deadline));
            }
            const lpTokenCurrencyId = newCurrencyId(this.api, pool.lpToken);
            const rewardCurrencies = yield this.api.query.farmingRewards.rewardCurrencies(lpTokenCurrencyId);
            const farmWithdrawalExtrinsic = this.api.tx.farming.withdraw(lpTokenCurrencyId, amount.toString(true), rewardCurrencies.size);
            const batchedExtrinsics = this.api.tx.utility.batchAll([farmWithdrawalExtrinsic, withdrawalExtrinsic]);
            return { extrinsic: batchedExtrinsics, event: withdrawalEvent };
        });
    }
    claimFarmingRewards(claimableRewards) {
        const claimExtrinsics = [];
        for (const [lpToken, rewards] of claimableRewards.entries()) {
            const lpTokenId = newCurrencyId(this.api, lpToken);
            rewards.forEach((rewardAmount) => {
                if (rewardAmount.toBig().gt(0)) {
                    const rewardCurrencyId = newCurrencyId(this.api, rewardAmount.currency);
                    const claimExtrinsic = this.api.tx.farming.claim(lpTokenId, rewardCurrencyId);
                    claimExtrinsics.push(claimExtrinsic);
                }
            });
        }
        const batchedExtrinsics = this.api.tx.utility.batchAll(claimExtrinsics);
        return { extrinsic: batchedExtrinsics, event: this.api.events.farming.RewardClaimed };
    }
}
//# sourceMappingURL=amm.js.map